import { Show, ShowFirstMatching } from '@components/Show';
import { tableCellClasses, tableHeaderClasses, tableRowClasses } from '@components/Table/components/classes';
import { RowsSkeleton } from '@components/Table/components/RowsSkeleton';
import { fuzzyFilter } from '@pages/User/helpers/filters/fuzzyFilter';
import { isEmptyValue, isNonNullish } from '@root/helpers';
import type { ColumnDef, Row } from '@tanstack/react-table';
import { flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import clsx from 'clsx';

interface TableProps<Item extends object> {
  className?: string;
  columns: ColumnDef<Item, any>[];
  data: Item[];
  emptyRowsLabel?: string;
  isLoading?: boolean;
  onRowClick?: (row: Item) => void;
  rowsSkeletonLength?: number;
}

export const Table = <Item extends object>({
  className,
  columns,
  data,
  emptyRowsLabel,
  isLoading,
  onRowClick,
  rowsSkeletonLength,
}: TableProps<Item>) => {
  const tableInstance = useReactTable<Item>({
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
    filterFns: { fuzzy: fuzzyFilter },
  });

  const hasInteractiveRows = isNonNullish(onRowClick);

  const handleRowClick = (row: Row<Item>) => {
    if (!hasInteractiveRows) {
      return;
    }

    return onRowClick?.(row.original);
  };

  return (
    <table role="table" className={clsx('w-full', className)}>
      <thead className="sticky top-0 z-10 bg-gray-50">
        {tableInstance.getHeaderGroups().map((headerGroup) => (
          <tr key={headerGroup.id} role="row">
            {headerGroup.headers.map((header) => {
              // header.getSize() substitutes tanstack's 150px default, so the raw columnDef.size is
              // what marks a column unsized.
              const { size } = header.column.columnDef;
              return (
                <th
                  key={header.id}
                  colSpan={header.colSpan}
                  role="columnheader"
                  className={tableHeaderClasses()}
                  style={size === undefined ? undefined : { width: size }}
                >
                  {flexRender(header.column.columnDef.header, header.getContext())}
                </th>
              );
            })}
          </tr>
        ))}
      </thead>
      <tbody>
        <ShowFirstMatching>
          <Show when={!isLoading && !isEmptyValue(emptyRowsLabel) && tableInstance.getRowModel().rows.length === 0}>
            <tr className={tableRowClasses({ className: 'p-3' })}>
              <td colSpan={columns.length} className="text-info-400 p-3 text-sm leading-5 font-normal">
                {emptyRowsLabel}
              </td>
            </tr>
          </Show>
          <Show when={isLoading}>
            <RowsSkeleton length={rowsSkeletonLength} />
          </Show>
          <Show when={true}>
            {tableInstance.getRowModel().rows.map((row) => {
              return (
                <tr
                  key={row.id}
                  role="row"
                  className={tableRowClasses({ hasInteractiveRows })}
                  onClick={() => handleRowClick(row)}
                >
                  {row.getVisibleCells().map((cell) => {
                    return (
                      <td role="cell" key={cell.id} className={tableCellClasses()}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </Show>
        </ShowFirstMatching>
      </tbody>
    </table>
  );
};
